import { NO_MATCHES_THIS_TIME_TITLE } from '../attendee-preview/constants';
import { cityLine } from '../event-discovery/constants';
import type { PublicEventDetail } from '../event-discovery/types';
import type { MemberRegistrationSummary } from '../event-orders/types';
import {
  FOLLOW_UP_INDEX_EMPTY_DESCRIPTION,
  indexChipFor,
  readyChipLabel,
} from '../follow-up/constants';
import type { FollowUpIndexEntry } from '../follow-up/types';
import type { MatchSet } from '../matching/types';

/**
 * **Your Events** — every word the page says, and the pure function that decides what it holds
 * (`MEM-052`).
 *
 * Copy and logic live here rather than in the components for the reason every member slice gives:
 * a sentence returned by a function can be asserted directly, and a rule inside JSX can only be
 * grepped for. This module imports types and other slices' alias-free constants only, so the test
 * runner loads it without a bundler.
 *
 * ## Where the page comes from
 *
 * `Web App.dc.html` (10 September), `showPeople`, lines 342–398 — the tab id is still `people`, a
 * leftover of the desktop note that called it *Your people*. It replaces the Matches and Follow up
 * indexes (Sumit, 2026-09-11, *"agree"*), which is why `MEM-025` AC-2 and `MEM-036` AC-1 were
 * reworded in the change that built it. Every row is built from the member's own data — their
 * registrations, their released match sets, their follow-up summaries — read from the three
 * endpoints that already served those indexes; nothing is new on the server.
 */

/**
 * `MEM-052` AC-1 — the heading, on its own. The prototype's line beneath it (*"Who to meet before
 * an event, who to follow up with after."*) was dropped at Brad's cleaned design (TW 44190018;
 * Sumit, 2026-09-24, *"ok with me."*): the heading and the navigation already say what the page is.
 */
export const YOUR_EVENTS_TITLE = 'Your Events';

/** The pill on the Next up card, and the one that says the member holds a place. */
export const NEXT_UP_PILL = 'Next up';
export const YOURE_GOING_PILL = "You're going";

/** `MEM-052` AC-2's three actions, the prototype's labels. */
export const SEE_YOUR_MATCHES_ACTION = 'See your matches';
export const EVENT_DETAILS_ACTION = 'Event details';
export const TICKET_ACTION = 'Ticket';

/** The two list headings and their orders, as the prototype writes them. */
export const UPCOMING_HEADING = 'My upcoming events';
export const UPCOMING_ORDER = 'Soonest first';
export const FOLLOW_UP_HEADING = 'My follow up';
export const FOLLOW_UP_ORDER = 'Latest first';

/** `MEM-052` AC-4's empty state, and its one action — both the prototype's. */
export const UPCOMING_EMPTY = 'Nothing else on your calendar yet.';
export const BROWSE_EVENTS_ACTION = 'Browse events';

/** `MEM-052` AC-8 — Brad's simplified first-event banner (TW 44213018; Sumit 2026-09-27). */
export const FIRST_EVENT_HEADING = 'Find your first event';
export const FIRST_EVENT_LINE =
  "Register for an event that fits you and we'll introduce you to the people worth meeting there.";
export const DISCOVER_AN_EVENT_ACTION = 'Discover an event';

/**
 * *My follow up* with nothing in it. **Not in the prototype**, whose list is every past event in
 * its fixture catalogue and so is never empty; the sentence is the follow-up index's own empty
 * copy, which a member met in the same place before this page existed.
 */
export const FOLLOW_UP_EMPTY = FOLLOW_UP_INDEX_EMPTY_DESCRIPTION;

/**
 * **What the Next up card and an upcoming row say about the member's matches** (`MEM-052` AC-3).
 *
 * The prototype says *"3 matches ready"* on the card while every row beneath it says *"Matches
 * open 48h before"* — both fixed text (`handoff-corrections.md` #124). AC-3 is the correction: the
 * card says what is true of **this member's own set** — the number released once it is released,
 * and when they open while it is not, never a count before release and never a score
 * (`MEM-024` AC-3).
 *
 * *When they open* is **shortly before the event**, which is as exact as a member is told
 * anywhere: the release interval is configuration rather than a constant (`MEM-MAT-002`), and the
 * matching slice keeps the schedule off every member payload, so an unreleased set, an opted-out
 * member and a set that does not exist all look alike (`event-matches-page.tsx`). *"48h"* would
 * be a number in code that the platform does not promise.
 */
export type MatchState =
  | { readonly kind: 'released'; readonly count: number }
  | { readonly kind: 'not-released' };

export function matchStateFor(eventId: string, sets: ReadonlyArray<MatchSet>): MatchState {
  const set = sets.find((candidate) => candidate.eventId === eventId);
  if (set === undefined) return { kind: 'not-released' };
  return { kind: 'released', count: set.suggestions.length };
}

/** The Next up card's band: a line and the sentence under it. */
export function matchBandCopy(state: MatchState): {
  readonly title: string;
  readonly description: string;
} {
  if (state.kind === 'not-released') {
    return {
      title: 'Your matches arrive shortly before the event',
      description: 'People worth meeting here, with a reason why.',
    };
  }
  if (state.count === 0) {
    // `MEM-027` AC-4's state: the set went out and this room held nobody worth putting in front of
    // the member. Its own heading, and the one reassurance its description ends on.
    return {
      title: NO_MATCHES_THIS_TIME_TITLE,
      description: 'You are still going, and the attendee list is there whenever you want a look.',
    };
  }
  return {
    title: state.count === 1 ? '1 match ready' : `${String(state.count)} matches ready`,
    description: 'People worth meeting here, with a reason why.',
  };
}

/** An upcoming row's pill — the same facts, in a pill's few words. */
export function matchPillLabel(state: MatchState): string {
  if (state.kind === 'not-released') return 'Matches open before the event';
  if (state.count === 0) return NO_MATCHES_THIS_TIME_TITLE;
  return state.count === 1 ? '1 match ready' : `${String(state.count)} matches ready`;
}

/**
 * A *My follow up* row's pill (`MEM-052` AC-5): *N follow-ups ready* or *All followed up* — the
 * follow-up index's own chip, so the two cannot disagree. A summary with nobody on it keeps that
 * index's third answer, *Recap only*: "All followed up" on a list that never had anybody on it
 * would congratulate a member for doing nothing (`MEM-035` AC-5).
 */
export function followUpPillLabel(entry: {
  readonly connectionCount: number;
  readonly followUpCount: number;
}): string {
  return indexChipFor(entry);
}

export function followUpIsPending(entry: { readonly followUpCount: number }): boolean {
  return readyChipLabel(entry.followUpCount) !== null;
}

/** One event on the page, as the Next up card and an upcoming row draw it. */
export interface UpcomingEventRow {
  readonly eventId: string;
  readonly name: string;
  readonly startsAt: string;
  readonly endsAt: string | null;
  readonly timezone: string | null;
  readonly venueName: string | null;
  readonly city: string | null;
  readonly atmosphere: string | null;
  readonly matches: MatchState;
}

/** One summary under *My follow up*. */
export interface FollowUpEventRow {
  readonly eventId: string;
  readonly name: string;
  /** When it ran: its end, or its start where no end was set. `null` for an event with neither. */
  readonly ranAt: string | null;
  readonly timezone: string | null;
  readonly city: string | null;
  readonly connectionCount: number;
  readonly followUpCount: number;
}

export interface YourEventsView {
  /** `MEM-052` AC-2 — `null` when nothing is coming up, and then no card is drawn at all. */
  readonly nextUp: UpcomingEventRow | null;
  /** `MEM-052` AC-4 — the *other* upcoming registrations, soonest first. */
  readonly upcoming: ReadonlyArray<UpcomingEventRow>;
  /** `MEM-052` AC-5 — most recent first, in the order the server sorted them. */
  readonly followUps: ReadonlyArray<FollowUpEventRow>;
  /** Every released match across the upcoming events — the push offer's reason (`MEM-040`). */
  readonly releasedMatchTotal: number;
}

/**
 * **A registration that is a place the member still holds** (`MEM-052` AC-7).
 *
 * `registered` (free) or `paid`, and not given up. A `refunded` or `refund-pending` row is a
 * place released — `MEM-023` AC-5 releases the seat before it moves the money — and a row with
 * `cancelledLabel` is a place the member cancelled; none of them is *upcoming*, whatever its date.
 */
export function holdsAPlace(registration: MemberRegistrationSummary): boolean {
  return (
    (registration.status === 'registered' || registration.status === 'paid') &&
    registration.cancelledLabel === null
  );
}

/**
 * **Is this event still ahead of the member?** — the page's one split.
 *
 * On the event's own `hasEnded` where its public page has been read — so an event under way is
 * still *Next up*, which is when Connect is for — and otherwise on the start instant, which is the
 * same instant in every timezone and needs none (`time-and-timezone.md`). The server keeps no
 * upcoming/past split for a member's registrations; this is it.
 */
export function isStillAhead(input: {
  readonly startsAt: string;
  readonly detail: Pick<PublicEventDetail, 'hasEnded'> | undefined;
  readonly now: Date;
}): boolean {
  if (input.detail !== undefined) return !input.detail.hasEnded;
  const start = Date.parse(input.startsAt);
  return Number.isFinite(start) && start > input.now.getTime();
}

/**
 * **What Your Events holds**, from the member's own three reads (`MEM-052` AC-2, AC-4, AC-5,
 * AC-7).
 *
 *   - **Next up** is the soonest confirmed registration still ahead; **My upcoming events** is
 *     the rest of them, soonest first — the prototype's `slice(1)`, sorted where the prototype
 *     only relied on its fixture's order (`handoff-corrections.md` #126).
 *   - **My follow up** is every event with a follow-up summary, in the server's order, which is
 *     most recent first (`MEM-036`). The prototype lists **every past event in its catalogue**,
 *     registered or not; a summary exists only for a member who was checked in (`MEM-FUP-001`),
 *     so this lists only events the member went to.
 *   - The city and atmosphere come from each event's public page (`details`), which a member's
 *     own payloads do not carry; an event whose page did not load draws without them.
 */
export function buildYourEventsView(input: {
  readonly registrations: ReadonlyArray<MemberRegistrationSummary>;
  readonly matchSets: ReadonlyArray<MatchSet>;
  readonly followUps: ReadonlyArray<FollowUpIndexEntry>;
  readonly details: ReadonlyMap<string, PublicEventDetail>;
  readonly now: Date;
}): YourEventsView {
  const ahead: UpcomingEventRow[] = [];
  const seen = new Set<string>();
  for (const registration of input.registrations) {
    if (!holdsAPlace(registration) || registration.startsAt === null) continue;
    if (seen.has(registration.eventId)) continue;
    const detail = input.details.get(registration.eventId);
    if (!isStillAhead({ startsAt: registration.startsAt, detail, now: input.now })) continue;
    seen.add(registration.eventId);
    ahead.push({
      eventId: registration.eventId,
      name: registration.eventName,
      startsAt: registration.startsAt,
      endsAt: detail?.endsAt ?? null,
      timezone: registration.timezone,
      venueName: registration.venueName ?? detail?.location.venueName ?? null,
      city: detail === undefined ? null : cityLine(detail.location),
      atmosphere: detail?.atmosphere?.displayName ?? null,
      matches: matchStateFor(registration.eventId, input.matchSets),
    });
  }
  ahead.sort((left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt));

  const followUps = input.followUps.map((entry): FollowUpEventRow => {
    const detail = input.details.get(entry.event.eventId);
    return {
      eventId: entry.event.eventId,
      name: entry.event.name,
      ranAt: entry.event.endsAt ?? entry.event.startsAt,
      timezone: entry.event.timezone,
      city: detail === undefined ? null : cityLine(detail.location),
      connectionCount: entry.connectionCount,
      followUpCount: entry.followUpCount,
    };
  });

  const releasedMatchTotal = ahead.reduce(
    (total, row) => total + (row.matches.kind === 'released' ? row.matches.count : 0),
    0,
  );

  return {
    nextUp: ahead[0] ?? null,
    upcoming: ahead.slice(1),
    followUps,
    releasedMatchTotal,
  };
}

/**
 * The event ids whose public pages the page reads for a city and an atmosphere: every place the
 * member holds that has not long passed, and every follow-up summary. Bounded on purpose — a
 * member's history of registrations grows without limit, and only the ones this page draws need
 * their page read.
 */
export function eventIdsToDescribe(input: {
  readonly registrations: ReadonlyArray<MemberRegistrationSummary>;
  readonly followUps: ReadonlyArray<FollowUpIndexEntry>;
  readonly now: Date;
}): ReadonlyArray<string> {
  // Two days back, so an event under way — or one that ran late into the night — is asked
  // about and can still be *Next up* by its own `hasEnded`.
  const horizon = input.now.getTime() - 2 * 24 * 60 * 60 * 1000;
  const ids = new Set<string>();
  for (const registration of input.registrations) {
    if (!holdsAPlace(registration) || registration.startsAt === null) continue;
    const start = Date.parse(registration.startsAt);
    if (Number.isFinite(start) && start >= horizon) ids.add(registration.eventId);
  }
  for (const entry of input.followUps) ids.add(entry.event.eventId);
  return [...ids];
}

/**
 * *"Thu, Jan 14"* — a row's date, in the event's own timezone (NFR `time-and-timezone#display`).
 * `null` for an instant that does not parse, so a row says nothing rather than *Invalid Date*.
 */
export function shortEventDate(instant: string | null, timezone: string | null): string | null {
  if (instant === null) return null;
  const parsed = Date.parse(instant);
  if (Number.isNaN(parsed)) return null;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: timezone ?? 'UTC',
  }).format(new Date(parsed));
}

/** A row's second line — the date, then the city — with nothing dangling where either is absent. */
export function rowWhenLine(date: string | null, city: string | null): string | null {
  const parts = [date, city].filter((part): part is string => part !== null && part.length > 0);
  return parts.length === 0 ? null : parts.join(' · ');
}

/** The Next up card's venue line: `The Wexford Room, Providence, RI`. */
export function venueAndCity(venueName: string | null, city: string | null): string | null {
  const parts = [venueName, city].filter(
    (part): part is string => part !== null && part.trim().length > 0,
  );
  return parts.length === 0 ? null : parts.join(', ');
}

/**
 * **What a column says when its read did not arrive** — TW 44145597
 * (https://ten243.teamwork.com/app/tasks/44145597).
 *
 * Brad's screenshot is a failure toast directly above *"Nothing else on your calendar yet."*: the
 * page saying *something went wrong* and *you have nothing on* in the same breath, when only one of
 * them can be true. `useYourEvents` read `registrations.data?.registrations ?? []`, so a read that
 * failed and a list that is genuinely empty became the same value before the view was built — and
 * the empty state's *Browse events* then invited a member with a place on Thursday to book another.
 *
 * So a column that could not be read says so instead of drawing its empty state, and offers the
 * read again. Short, because the sentence sits inside a card in a column, and the toast beside it
 * already carries the screen's own wording.
 */
export const LIST_UNAVAILABLE = 'We could not load this list.';

/** See {@link LIST_UNAVAILABLE}. */
export const RETRY_ACTION = 'Try again';
